"use client";

import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { postsAPI, socialUtils, handleApiError } from "../../services/api";
import { getUserTypeColor } from "../../utils/userTypeUtils";
import { getUserPlaceholderImage } from "../../utils/imageUtils";
import { usePostUploadActions } from "../../contexts/PostUploadContext";
import "./ChannelPostCreation.css";

const ChannelPostCreation = ({ channel, onPostCreated }) => {
  const { t, i18n } = useTranslation();
  const { getProfileImageUrl, getUserType, user: currentUser } = useAuth();
  const { startUpload } = usePostUploadActions();

  const [postContent, setPostContent] = useState("");
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [postType, setPostType] = useState("post"); // post, question, poll
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pollOptions, setPollOptions] = useState(["", ""]);
  const [pollDuration, setPollDuration] = useState(7); // days
  const fallbackTimeoutRef = useRef(null);
  const postAddedRef = useRef(false);

  // Listen for post upload completion to call onPostCreated callback
  useEffect(() => {
    if (!onPostCreated || !channel?.id) return;

    const handlePostUploadCompleted = (event) => {
      const { post } = event.detail;
      if (!post) return;

      // Check if this post belongs to our channel
      const postChannelId = post.channel_id || post.channelId || post.channel?.id || post.channel?.channel_id;
      
      // More lenient check: if we're on a channel page and the post was just created, accept it
      // even if channel_id format doesn't match exactly
      const channelMatches = postChannelId && Number(postChannelId) === Number(channel.id);
      const isRecentPost = post.created_at || post.createdAt || post.id;
      
      if (channelMatches || (isRecentPost && !postChannelId)) {
        // Transform and call the callback
        const transformedPost = socialUtils.transformPostData(post);
        if (transformedPost && onPostCreated) {
          onPostCreated(transformedPost);
          postAddedRef.current = true; // Mark as added
          
          // Clear fallback timeout since post was added successfully
          if (fallbackTimeoutRef.current) {
            clearTimeout(fallbackTimeoutRef.current);
            fallbackTimeoutRef.current = null;
          }
        }
      }
    };

    window.addEventListener("post-upload-completed", handlePostUploadCompleted);
    return () => {
      window.removeEventListener("post-upload-completed", handlePostUploadCompleted);
    };
  }, [channel?.id, onPostCreated]);

  // Get current user type color
  const currentUserType = getUserType();
  const userTypeColor = getUserTypeColor(currentUserType);

  const handleMediaSelect = (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    setSelectedFiles(prev => [...prev, ...imageFiles]);

    imageFiles.forEach((file, index) => {
      const previewUrl = URL.createObjectURL(file);
      setSelectedMedia(prev => [...prev, {
        url: previewUrl,
        file: file,
        type: 'image'
      }]);
    });
  };

  const handleRemoveMedia = (index) => {
    setSelectedMedia(prev => prev.filter((_, i) => i !== index));
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddPollOption = () => {
    setPollOptions(prev => [...prev, ""]);
  };

  const handlePollOptionChange = (index, value) => {
    setPollOptions(prev => prev.map((opt, i) => i === index ? value : opt));
  };

  const handleRemovePollOption = (index) => {
    if (pollOptions.length > 2) {
      setPollOptions(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!postContent.trim() && selectedMedia.length === 0 && postType !== "poll") {
      return;
    }

    if (postType === "poll" && pollOptions.filter(opt => opt.trim()).length < 2) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare post data
      const normalizedType =
        postType === "post"
          ? selectedMedia.length > 0
            ? "post"
            : "post"
          : postType;

      const postData = {
        content: postContent,
        channel_id: channel.id,
        post_type: normalizedType,
        type: normalizedType, // legacy compatibility
        // Add files if post has media
        files: postType === "post" && selectedFiles.length > 0 ? selectedFiles : undefined,
        // Add poll data if poll type
        poll_options: postType === "poll" ? pollOptions.filter(opt => opt.trim()) : undefined,
        poll_duration: postType === "poll" ? pollDuration : undefined
      };

      // Store channel ID for later verification
      const currentChannelId = channel.id;
      postAddedRef.current = false; // Reset flag for new post

      // Hand off to background upload (non-blocking)
      startUpload(postData).catch((error) => {
        console.error('[ChannelPostCreation] Upload failed:', error);
      });

      // Fallback: If event doesn't fire within delay, trigger a manual refresh
      // This ensures posts appear even if the event system fails
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
      }
      const delay = selectedFiles.length > 0 ? 5000 : 3000;
      fallbackTimeoutRef.current = setTimeout(() => {
        // Only trigger reload if post wasn't already added via event
        if (!postAddedRef.current) {
          // Dispatch a custom event to trigger post reload
          window.dispatchEvent(new CustomEvent('channel-post-created', {
            detail: { channelId: currentChannelId }
          }));
        }
        fallbackTimeoutRef.current = null;
      }, delay);

      // Reset form immediately
      setPostContent("");
      setSelectedMedia([]);
      setSelectedFiles([]);
      setPostType("post");
      setIsExpanded(false);
      setPollOptions(["", ""]);
      setPollDuration(7);


      // In a channel, we might want to tell the parent that we initiated a post, 
      // but without the actual post data yet (since it's in background).
      // If onPostCreated expects a real post object, we might have a problem.
      // However, most of the time it's used to refresh the feed or optimistically add.
      // For now, let's just reset.

    } catch (error) {
      alert("Failed to initiate post creation. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isArabicText = (text) => {
    if (!text || text.trim() === "") return false;
    const firstChar = text.trim().charAt(0);
    const arabicRegex =
      /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
    return arabicRegex.test(firstChar);
  };

  return (
    <div className="channel-post-creation" style={{ '--user-type-color': userTypeColor }}>
      <div className="channel-post-creation-header">
        <img
          src={getProfileImageUrl() || getUserPlaceholderImage(currentUser)}
          alt="Profile"
          className="channel-post-creation-profile-img"
          onError={(e) => {
            e.target.src = getUserPlaceholderImage(currentUser);
          }}
        />
        <div className="channel-post-creation-header-info">
          <span className="channel-post-creation-channel-name">
            {t("channelPostCreation.postingTo", "Posting to {{name}}", {
              name: channel.name,
            })}
          </span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="channel-post-creation-form">
        <div className="channel-post-type-selector">
          <button
            type="button"
            className={`post-type-btn ${postType === "post" ? "active" : ""}`}
            onClick={() => setPostType("post")}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
            {t("channelPostCreation.types.post", "Post")}
          </button>
          <button
            type="button"
            className={`post-type-btn ${postType === "question" ? "active" : ""}`}
            onClick={() => setPostType("question")}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
              <circle cx="12" cy="17" r="1"></circle>
            </svg>
            {t("channelPostCreation.types.question", "Question")}
          </button>
          <button
            type="button"
            className={`post-type-btn ${postType === "poll" ? "active" : ""}`}
            onClick={() => setPostType("poll")}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="20" x2="18" y2="10"></line>
              <line x1="12" y1="20" x2="12" y2="4"></line>
              <line x1="6" y1="20" x2="6" y2="14"></line>
            </svg>
            {t("channelPostCreation.types.poll", "Poll")}
          </button>
        </div>

        <div className="channel-post-creation-input-container">
          <textarea
            value={postContent}
            onChange={(e) => setPostContent(e.target.value)}
            onFocus={() => setIsExpanded(true)}
            placeholder={
              postType === "question"
                ? t(
                  "channelPostCreation.placeholders.question",
                  "Ask a question..."
                )
                : postType === "poll"
                  ? t(
                    "channelPostCreation.placeholders.poll",
                    "What would you like to poll about?"
                  )
                  : t(
                    "channelPostCreation.placeholders.post",
                    "What's on your mind? (You can add photos too)"
                  )
            }
            className="channel-post-creation-input"
            dir={
              isArabicText(postContent) || i18n.language?.startsWith("ar")
                ? "rtl"
                : "ltr"
            }
            style={{
              textAlign:
                isArabicText(postContent) || i18n.language?.startsWith("ar")
                  ? "right"
                  : "left",
            }}
            rows={isExpanded ? 4 : 2}
          />

          {/* Image Preview */}
          {postType === "post" && selectedMedia.length > 0 && (
            <div className="channel-post-media-preview">
              {selectedMedia.map((media, index) => (
                <div key={index} className="channel-post-media-item">
                  <img src={media.url} alt={`Preview ${index + 1}`} />
                  <button
                    type="button"
                    className="channel-post-media-remove"
                    onClick={() => handleRemoveMedia(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Poll Options */}
          {postType === "poll" && (
            <div className="channel-post-poll-options">
              <div className="channel-post-poll-options-list">
                {pollOptions.map((option, index) => (
                  <div key={index} className="channel-post-poll-option">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => handlePollOptionChange(index, e.target.value)}
                      placeholder={t(
                        "channelPostCreation.poll.optionPlaceholder",
                        "Option {{index}}",
                        { index: index + 1 }
                      )}
                      className="channel-post-poll-option-input"
                    />
                    {pollOptions.length > 2 && (
                      <button
                        type="button"
                        className="channel-post-poll-option-remove"
                        onClick={() => handleRemovePollOption(index)}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="channel-post-poll-add-option"
                onClick={handleAddPollOption}
              >
                {t("channelPostCreation.poll.addOption", "+ Add option")}
              </button>
              <div className="channel-post-poll-duration">
                <label>
                  {t(
                    "channelPostCreation.poll.durationLabel",
                    "Poll Duration (days):"
                  )}
                </label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={pollDuration}
                  onChange={(e) => setPollDuration(parseInt(e.target.value) || 7)}
                  className="channel-post-poll-duration-input"
                />
              </div>
            </div>
          )}

        </div>


        <div className="channel-post-creation-actions">
          <div className="channel-post-creation-media-actions">
            {postType === "post" && (
              <label className="channel-post-creation-media-btn">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleMediaSelect}
                  style={{ display: "none" }}
                />
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  <polyline points="21 15 16 10 5 21"></polyline>
                </svg>
                {t("channelPostCreation.media.photos", "Photos")}
              </label>
            )}
          </div>
          <button
            type="submit"
            className="channel-post-creation-submit-btn"
            disabled={isSubmitting || (!postContent.trim() && selectedMedia.length === 0 && postType !== "poll")}
            style={{
              '--user-type-color': userTypeColor
            }}
          >
            {isSubmitting
              ? t("channelPostCreation.submit.posting", "Posting...")
              : t("channelPostCreation.submit.post", "Post")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChannelPostCreation;

